import { RefObject, useCallback, useMemo } from "react";
import FileManager, {
  FileManagerProps,
} from "domains/file-manager/components/FileManager";
import { useFileModelHandler } from "domains/file-manager/hooks/useFileModelHandler";
import { FileModelType } from "domains/file-manager/interfaces";
import ModelListEmptyState from "domains/models/components/ModelList/ModelListEmptyState";
import { BLEND_CATEGORIES } from "domains/models/constants/blend";
import { ModelTag } from "domains/models/constants/tags";
import { useCreateBlendContext } from "domains/models/contexts/CreateBlendProvider";
import useAllModels, {
  UseAllModelsArgs,
} from "domains/models/hooks/useAllModels";
import useRouter from "domains/navigation/hooks/useRouter";
import { tagIdToObject } from "domains/tags/utils/tagIdToLabel";

import { Box, Heading, Text, VStack } from "@chakra-ui/react";

import BlendBreadcrumb from "./BlendBreadcrumb";
import BlendCategoriesList from "./BlendCategoriesList";

interface BlendCategoriesViewProps {
  contentScrollRef: RefObject<HTMLDivElement>;
  categoryId: string;
  subCategoryId?: string;
}

export default function BlendCategoriesView({
  contentScrollRef,
  categoryId,
  subCategoryId,
}: BlendCategoriesViewProps) {
  const router = useRouter();
  const { type, search, concepts, addConcept, removeConcept } =
    useCreateBlendContext();

  const category = tagIdToObject(categoryId);
  const subCategory = category ? tagIdToObject(subCategoryId) : undefined;
  const parentCategoryId = subCategory?.id ?? category?.id;

  const parentCategory = useMemo(
    () =>
      BLEND_CATEGORIES.find((category) => category.tagId === parentCategoryId),
    [parentCategoryId]
  );
  const subCategories = useMemo(
    () => (parentCategoryId ? parentCategory?.categories : BLEND_CATEGORIES),
    [parentCategoryId, parentCategory?.categories]
  );

  const allModelsArgs: UseAllModelsArgs = useMemo(
    () => ({
      typesFilter: [type === "sd-xl" ? "sd-xl-lora" : "flux.1-lora"],
      searchTerm: search,
      statusesFilter: ["trained"],
      tagsFilter: [subCategoryId || categoryId] as ModelTag[],
    }),
    [type, search, subCategoryId, categoryId]
  );

  const publicConcepts = useAllModels({
    privacyFilter: "public",
    ...allModelsArgs,
  });

  // ----------------------------------

  const handleAdd = useCallback(
    (file: FileModelType) => addConcept(file.meta.id),
    [addConcept]
  );

  const handleUndoAdd = useCallback(
    (file: FileModelType) => removeConcept(file.meta.id),
    [removeConcept]
  );

  const handleOpen = useCallback(
    (file: FileModelType) => {
      void router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            selectedModelId: file.meta.id,
            ...(categoryId ? { categoryId } : {}),
            ...(subCategoryId ? { subCategoryId } : {}),
          },
        },
        undefined,
        { scroll: false }
      );
    },
    [router, categoryId, subCategoryId]
  );

  // ----------------------------------

  const modelFileHandler = useFileModelHandler({
    conceptIdsWithoutAdd: concepts.map((concept) => concept.modelId),
    onAdd: handleAdd,
    onUndoAdd: handleUndoAdd,
    emptyState: (
      <Box p={4}>
        <ModelListEmptyState reason="no-search-results" />
      </Box>
    ),
    withLink: false,
  });

  const fileManagerProps: {
    fileHandlers: FileManagerProps["fileHandlers"];
    options: FileManagerProps["options"];
    scrollRef?: RefObject<HTMLDivElement>;
  } = useMemo(
    () => ({
      fileHandlers: {
        model: { ...modelFileHandler, onOpen: handleOpen },
      },
      options: {
        canSelect: false,
        canChangeView: false,
        canChangeNumberOfColumns: false,
        showFileNames: "always",
        numberOfColumns: 5,
        withoutTopBar: true,
      },
      scrollRef: contentScrollRef,
    }),
    [modelFileHandler, handleOpen, contentScrollRef]
  );

  // ----------------------------------

  return (
    <VStack align="stretch" w="full" p={6} spacing={4}>
      <BlendBreadcrumb category={category} subCategory={subCategory} />

      {(subCategory || category) && (
        <Heading
          w="full"
          color="textPrimary"
          textAlign="left"
          size={["md", "lg"]}
        >
          {subCategory?.label ?? category?.label}
        </Heading>
      )}

      {!!subCategories?.length && (
        <BlendCategoriesList
          parentCategoryId={parentCategoryId}
          categories={subCategories}
        />
      )}

      <VStack align="stretch" spacing={2}>
        {!subCategoryId && !!subCategories?.length && (
          <Text color="textSecondary" textAlign="left" size="body.bold.md">
            Models
          </Text>
        )}

        <FileManager
          files={publicConcepts.files}
          isLoading={publicConcepts.isLoading}
          loadMore={publicConcepts.loadMore}
          hasMore={publicConcepts.hasMore}
          {...fileManagerProps}
        />
      </VStack>
    </VStack>
  );
}
